import { H2, P, B, UL, LI, Steps, Step, Callout, SiNo } from "./ui";
import { CosteVideo } from "./precios";

export const Portada = () => (
  <>
    <P>
      YTMethod convierte un título en un documental narrado y montado, listo para subir a
      YouTube. Tú decides de qué va el vídeo; el sistema escribe el guion, lo narra, busca el
      material de archivo que ilustra cada frase, lo monta y te lo entrega.
    </P>
    <P>
      Está pensado para <B>canales faceless de documental</B>: historia, misterio, biografías,
      divulgación. Vídeos de voz en off sobre metraje de archivo, sin cámara y sin plató.
    </P>

    <H2 id="que-es">Qué es y qué no es</H2>
    <P>
      Antes de que gastes créditos aprendiéndolo por tu cuenta, conviene que sepas dónde encaja
      esta herramienta y dónde no.
    </P>
    <SiNo
      si={[
        "Documentales narrados de 5 a 70 minutos con voz en off",
        "Canales faceless que publican con regularidad",
        "Temas que tienen material visual disponible: historia, ciencia, biografías, sucesos",
        "Producir en volumen sin montar a mano cada vídeo",
        "Programar un calendario y que los vídeos se creen solos",
      ]}
      no={[
        "Vídeos con presentador a cámara o entrevistas",
        "Contenido de última hora: el material de archivo aún no existe",
        "Temas sin registro visual (un suceso muy reciente, algo muy local)",
        "Animación, motion graphics de autor o edición cuadro a cuadro",
        "Shorts verticales como formato principal",
      ]}
    />

    <H2 id="como-funciona">Cómo funciona, en tres pasos</H2>
    <Steps>
      <Step n="1" title="Dices de qué va">
        Un título y, si quieres, una descripción. Puedes escribirlo tú, elegir una idea sugerida o
        dejar que el calendario lo haga por ti.
      </Step>
      <Step n="2" title="El sistema produce">
        Guion, narración con voz real, búsqueda de material para cada plano y plan de montaje. Es
        la parte larga: entre 15 y 40 minutos según la duración.
      </Step>
      <Step n="3" title="Revisas y renderizas">
        Ves el material escena por escena antes de gastar el render. Cambias lo que no te guste y
        entonces se monta el vídeo final.
      </Step>
    </Steps>
    <Callout tipo="info" titulo="Siempre hay parada de revisión">
      El vídeo nunca se renderiza sin que lo apruebes. Revisar es gratis; renderizar cuesta
      créditos. Es a propósito: sale mucho más barato descubrir que una escena no encaja antes
      de montarla que después.
    </Callout>

    <H2 id="cuanto-cuesta">Cuánto cuesta un vídeo</H2>
    <P>
      Todo se mide en <B>créditos</B>, y un crédito equivale aproximadamente a un segundo de vídeo
      terminado. Un documental de 15 minutos consume del orden de <CosteVideo minutos={15} />;
      uno de 30, sobre <CosteVideo minutos={30} />.
    </P>
    <P>
      No es exacto: un vídeo cuyo tema tiene poco material de archivo necesita más búsquedas y más
      imágenes generadas, y consume más. La sección de <B>Créditos y planes</B> lo explica en detalle.
    </P>

    <H2 id="por-donde-empezar">Por dónde empezar</H2>
    <UL>
      <LI><B>Nunca lo has usado:</B> ve a «Tu primer vídeo» y sigue los pasos. Se hace en un rato.</LI>
      <LI><B>Ya tienes un vídeo hecho:</B> «La parada de revisión» te ahorrará créditos desde el primer día.</LI>
      <LI><B>Vas a publicar en serio:</B> «Elegir un nicho» y «Títulos que funcionan» valen más que cualquier ajuste técnico.</LI>
    </UL>
  </>
);

export const Canal = () => (
  <>
    <P>
      Todo cuelga de un <B>canal</B>. Un canal es la identidad de lo que publicas: su idioma, su
      voz, su temática y su estilo visual. Los vídeos heredan esa configuración, así que cuanto
      mejor la definas, menos tendrás que corregir después en cada vídeo.
    </P>

    <H2 id="crear">Crear el canal</H2>
    <Steps>
      <Step n="1" title="Nombre y temática">
        La temática no es decorativa: guía al guionista y a la búsqueda de material. «Misterios sin
        resolver del siglo XX» produce vídeos muy distintos de «Historia militar».
      </Step>
      <Step n="2" title="Idioma">
        Determina el idioma del guion, de la narración y de los rótulos. Cambiarlo después no
        traduce los vídeos ya hechos.
      </Step>
      <Step n="3" title="Voz">
        La voz por defecto de todos los vídeos del canal. Puedes escuchar muestras antes de elegir
        y cambiarla vídeo a vídeo si un tema pide otro tono.
      </Step>
    </Steps>

    <Callout tipo="truco" titulo="Sé concreto con la temática">
      Una temática amplia («curiosidades») da guiones genéricos y búsquedas de material imprecisas,
      porque el sistema no sabe qué mundo visual buscar. Una estrecha («naufragios del Atlántico
      Norte») produce vídeos más afilados y con mejor material.
    </Callout>

    <H2 id="cuantos">Cuántos canales puedes tener</H2>
    <P>
      Depende de tu plan: uno en Starter, tres en Creator y diez en Pro. Los créditos son
      del <B>usuario</B>, no del canal: si tienes tres canales, comparten el mismo saldo.
    </P>

    <H2 id="youtube">Conectar YouTube</H2>
    <P>
      Conectar el canal de YouTube sirve para dos cosas: subir los vídeos terminados sin descargarlos
      y ver las estadísticas reales del canal dentro de YTMethod.
    </P>
    <P>
      Se hace desde la ficha del canal, con la cuenta de Google que administra ese canal de YouTube.
      Puedes desconectarlo cuando quieras.
    </P>
    <Callout tipo="aviso" titulo="Un vídeo subido no se puede des-subir desde aquí">
      Al publicar, el vídeo va a tu canal de YouTube con la visibilidad que elijas. Si te
      arrepientes, tendrás que borrarlo desde YouTube. Si tienes dudas, súbelo como
      <B> no listado</B> y cámbialo a público cuando lo hayas revisado allí.
    </Callout>
  </>
);

export const PrimerVideo = () => (
  <>
    <P>
      Vamos a hacer un vídeo de principio a fin. Necesitas un canal creado y un plan de pago
      activo: escribir el guion y grabar la narración consumen créditos, así que producir requiere
      suscripción.
    </P>

    <H2 id="pasos">Los pasos</H2>
    <Steps>
      <Step n="1" title="Pulsa «Producción en 1 clic»">
        Desde la ficha del canal. Es el flujo completo: guion, narración, material y montaje.
      </Step>
      <Step n="2" title="Escribe el título">
        El título manda sobre todo lo demás: de él salen el enfoque del guion y el material que se
        busca. «10 celebridades que murieron en circunstancias extrañas» funciona mucho mejor que
        «Muertes famosas».
      </Step>
      <Step n="3" title="Elige la duración">
        Para el primero, quédate corto: 5 o 10 minutos. Se produce antes y gastas menos mientras
        aprendes cómo queda.
      </Step>
      <Step n="4" title="Lánzalo y espera">
        Puedes cerrar la pestaña. El trabajo sigue en el servidor y verás su avance en la píldora
        de la cabecera desde cualquier pantalla.
      </Step>
      <Step n="5" title="Revisa el material">
        Cuando termine de buscar, el vídeo se para y te enseña cada escena con sus clips. Aquí es
        donde decides si merece la pena renderizar.
      </Step>
      <Step n="6" title="Renderiza y publica">
        El render tarda según la duración. Al acabar puedes descargarlo o subirlo a YouTube desde
        la misma pantalla.
      </Step>
    </Steps>

    <H2 id="cuanto-tarda">Cuánto tarda</H2>
    <P>
      El guion, la narración y la búsqueda de material llevan entre 15 y 40 minutos según la
      duración del vídeo y lo difícil que sea encontrar material del tema. El render va aparte y
      depende de la cola: se renderiza <B>un vídeo cada vez</B> en todo el sistema, así que si
      hay otro por delante, el tuyo espera su turno.
    </P>

    <Callout tipo="info" titulo="No hace falta que te quedes mirando">
      Todo ocurre en el servidor. Puedes cerrar el navegador y volver más tarde: encontrarás el
      vídeo en el punto en que estuviera. Si algo se corta a mitad, no pierdes lo que ya estaba hecho.
    </Callout>

    <H2 id="que-esperar">Qué esperar del primero</H2>
    <P>
      El primer vídeo casi nunca es el bueno, y no es culpa tuya: estás aprendiendo qué títulos
      producen buenos guiones y qué temas tienen material disponible. Míralo como una prueba de
      calibración.
    </P>
    <UL>
      <LI>Si el guion no te convence, el problema suele estar en el título, no en el guion.</LI>
      <LI>Si el material no pega con lo que se cuenta, el tema probablemente tiene poco archivo visual.</LI>
      <LI>Si algunas escenas quedan cortas de material, revisa «Cuando falta material» en Problemas.</LI>
    </UL>
  </>
);
